/* eslint-disable jsx-a11y/no-static-element-interactions -- onClick handlers on div wrappers for event propagation control */
import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Button, DatePicker, Drawer, Select, Table, Empty } from 'antd-v5';
import moment from 'moment';
import dayjs from 'dayjs';
import StandardLayoutV2 from '../../../../Layout/StandardLayoutV2';
import { getRequest } from '../../../../../core/apiRequests';
import ConversationWindow from '../../../../../components/Layout/chat/chat';
import {
  getClientChat,
  resetMessaging,
  setChatReviewLink,
  setClientAssociate,
} from '../../../../../reducers/messaging';
import { resetToday } from '../../../../../reducers/today';
import useSessionState from '../../../../../hooks/useSessionState';
import getClientPaymentStatus from '../../../../../components/Layout/GetClientPaymentStatus';
import MessageConfigToday from '../../../../../components/Routes/Today/children/MessageConfigToday';
import '../../../../../components/Routes/Today/today.css';

const { RangePicker } = DatePicker;

// --- Sort helpers (server-side sorting) ---
const DEFAULT_SORT = { sortBy: 'close_date', sortOrder: 'ASC' };

const SORT_FIELD_BY_COLUMN = {
  close_date: 'close_date',
  created_date: 'created_date',
  client_name: 'client_name',
  name: 'name',
  amount: 'amount',
  stage_name: 'stage_name',
  created_by: 'created_by',
};

const resolveSort = (columnKey, antdOrder) => ({
  sortBy: SORT_FIELD_BY_COLUMN[columnKey] || DEFAULT_SORT.sortBy,
  sortOrder: antdOrder === 'ascend' ? 'ASC' : 'DESC',
});

const flipSort = order => (order === 'ASC' ? 'DESC' : 'ASC');

const toDayjsRange = range => (range ? range.map(date => dayjs(date)) : null);

const getColumnSortOrder = (columnKey, sortBy, sortOrder) => {
  if (SORT_FIELD_BY_COLUMN[columnKey] !== sortBy) return null;
  return sortOrder === 'ASC' ? 'ascend' : 'descend';
};

const AllSalesOpp = ({ auth }) => {
  // Filters and sort persist for the tab so they survive drilling into a client and coming back
  const storageKey = field => (auth.userId ? `all-sales-opp:${auth.userId}:${field}` : null);
  const [showChat, setShowChat] = useState(false);
  const [pageSize, setPageSize] = useSessionState(storageKey('pageSize'), 25);
  const [page, setPage] = useState(1);
  const [isShown, setIsShown] = useState('');
  const [selectedAssociateId, setSelectedAssociateId] = useSessionState(storageKey('associateId'), null);

  // Sort state
  const [sortBy, setSortBy] = useSessionState(storageKey('sortBy'), DEFAULT_SORT.sortBy);
  const [sortOrder, setSortOrder] = useSessionState(storageKey('sortOrder'), DEFAULT_SORT.sortOrder);

  // Filter state
  const [selectedOpportunityType, setSelectedOpportunityType] = useSessionState(storageKey('opportunityType'), null);
  const [createdDateRange, setCreatedDateRange] = useSessionState(storageKey('createdDateRange'), null, {
    deserialize: toDayjsRange,
  });
  const [dueDateRange, setDueDateRange] = useSessionState(storageKey('dueDateRange'), null, {
    deserialize: toDayjsRange,
  });

  const salesName = useMemo(
    () => auth?.merchantConfig?.SALES_OPP_NAME || 'Sales Opportunities',
    [auth?.merchantConfig],
  );

  const { data: associates } = useQuery(
    ['associates-list'],
    async () => {
      const reqObj = {
        params: ['associates'],
        loadingStatusOff: true,
      };
      return getRequest(reqObj);
    },
    {
      enabled: !!auth.merchantId && auth.role !== 'ASSOCIATE',
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  // Fetch opportunity types (sales pipelines) for filter dropdown
  const { data: opportunityTypes } = useQuery(
    ['salesPipelineTypes', auth.merchantId, auth.storeId],
    async () => {
      const reqObj = {
        params: ['report', auth.merchantId, auth.storeId || 0, 'salesPipeline_stages'],
        query: { filterPipeline: '' },
        loadingStatusOff: true,
      };
      const data = await getRequest(reqObj);
      if (data?.[0]?.pipeArray) {
        return data[0].pipeArray.map(pipe => ({
          label: pipe.name,
          value: pipe.pipeline_id,
        }));
      }
      return [];
    },
    {
      enabled: !!auth.merchantId,
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  // Serialize date ranges for query key and API params
  const createdStart = createdDateRange?.[0]?.format('YYYYMMDD') || null;
  const createdEnd = createdDateRange?.[1]?.format('YYYYMMDD') || null;
  const dueStart = dueDateRange?.[0]?.format('YYYYMMDD') || null;
  const dueEnd = dueDateRange?.[1]?.format('YYYYMMDD') || null;

  const { data: salesData, isFetching } = useQuery(
    [
      'allSalesOpp',
      page,
      pageSize,
      selectedAssociateId,
      sortBy,
      sortOrder,
      selectedOpportunityType,
      createdStart,
      createdEnd,
      dueStart,
      dueEnd,
    ],
    async () => {
      try {
        const reqObj = {
          params: ['today', auth.merchantId, 'getSalesOpps', auth.userId],
          query: {
            pageStart: page,
            pageSize,
            sortBy,
            sortOrder,
            ...(auth.role !== 'ASSOCIATE' && selectedAssociateId ? { associateId: selectedAssociateId } : {}),
            ...(selectedOpportunityType ? { opportunityType: selectedOpportunityType } : {}),
            ...(createdStart ? { createdStartDate: createdStart } : {}),
            ...(createdEnd ? { createdEndDate: createdEnd } : {}),
            ...(dueStart ? { dueStartDate: dueStart } : {}),
            ...(dueEnd ? { dueEndDate: dueEnd } : {}),
          },
        };
        const response = await getRequest(reqObj);
        if (response?.salesOpps) {
          return {
            data: response.salesOpps,
            salesStages: response.salesStages || [],
            total: response.total || 0,
          };
        }
        return { data: [], salesStages: [], total: 0 };
      } catch (error) {
        console.error('Error fetching sales opportunities:', error);
        return { data: [], salesStages: [], total: 0 };
      }
    },
    {
      refetchOnWindowFocus: false,
      initialData: { data: [], salesStages: [], total: 0 },
      enabled: !!auth?.merchantId,
    },
  );

  const green = obj => {
    if (salesData?.data?.length > 0 && salesData?.salesStages?.length > 0) {
      const filteredSalesOpps =
        salesData.salesStages.filter(opp => opp.sales_pipeline_id === obj.sales_pipeline_id) || [];
      if (filteredSalesOpps.length > 0) {
        const small = filteredSalesOpps.reduce((prev, curr) => (prev.weight < curr.weight ? prev : curr));
        return small.id;
      }
    }
    return null;
  };

  const fullNameCheck = (first, last) => {
    if (last) {
      return `${first} ${last}`.trim();
    }
    return first;
  };

  const closeChat = () => {
    setShowChat(false);
    resetMessaging();
    resetToday();
  };

  const getReviewLink = async reviewStoreId => {
    if (reviewStoreId) {
      const reqObj = { params: ['chats', reviewStoreId, 'getReviewLink'] };
      const linkData = await getRequest(reqObj);
      if (linkData.length > 0) {
        setChatReviewLink(linkData[0].review_link);
      }
    }
  };

  const openChatModal = async client => {
    await getClientChat(client);
    await getReviewLink(client.store_id);
    setClientAssociate(client.associateCount);
    getClientPaymentStatus({ client, auth });
    setShowChat(true);
  };

  const hasActiveFilters = selectedAssociateId || selectedOpportunityType || createdDateRange || dueDateRange;

  const clearFilters = () => {
    setSelectedAssociateId(null);
    setSelectedOpportunityType(null);
    setCreatedDateRange(null);
    setDueDateRange(null);
    setPage(1);
  };

  const handleTableChange = useCallback((pagination, _filters, sorter, extra) => {
    if (extra.action === 'sort') {
      const order = Array.isArray(sorter) ? sorter[0]?.order : sorter?.order;
      const columnKey = Array.isArray(sorter) ? sorter[0]?.columnKey : sorter?.columnKey;
      if (order) {
        const next = resolveSort(columnKey, order);
        setSortBy(next.sortBy);
        setSortOrder(next.sortOrder);
      } else {
        setSortOrder(flipSort);
      }
      setPage(1);
    } else if (extra.action === 'paginate') {
      setPage(pagination.current);
      setPageSize(pagination.pageSize);
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        title: 'Due Date',
        dataIndex: 'close_date',
        key: 'close_date',
        width: '10%',
        sorter: true,
        sortOrder: getColumnSortOrder('close_date', sortBy, sortOrder),
        render: t => {
          if (t) {
            return moment(t, 'YYYYMMDD').format('MM/DD/YYYY');
          }
          return null;
        },
      },
      {
        title: 'Created Date',
        dataIndex: 'created_date',
        key: 'created_date',
        width: '10%',
        sorter: true,
        sortOrder: getColumnSortOrder('created_date', sortBy, sortOrder),
        render: t => {
          if (t) {
            return moment(t, 'YYYYMMDD').format('MM/DD/YYYY');
          }
          return null;
        },
      },
      {
        title: 'Client Name',
        dataIndex: 'client_name',
        key: 'client_name',
        width: '13%',
        sorter: true,
        sortOrder: getColumnSortOrder('client_name', sortBy, sortOrder),
        render: (text, record) => <Link to={`/Clients?client=${record.client_id}`}>{text}</Link>,
      },
      {
        title: 'Description',
        dataIndex: 'name',
        key: 'name',
        width: '17%',
        sorter: true,
        sortOrder: getColumnSortOrder('name', sortBy, sortOrder),
        render: (text, record) => <Link to={`/Clients?client=${record.client_id}&oppId=${record.id}`}>{text}</Link>,
      },
      {
        title: 'Price Value',
        dataIndex: 'amount',
        key: 'amount',
        width: '9%',
        sorter: true,
        sortOrder: getColumnSortOrder('amount', sortBy, sortOrder),
        render: amount => {
          if (amount) {
            return `$${amount}`;
          }
          return '$0';
        },
      },
      {
        title: 'Stage',
        dataIndex: 'stage_name',
        key: 'stage_name',
        width: '11%',
        align: 'center',
        sorter: true,
        sortOrder: getColumnSortOrder('stage_name', sortBy, sortOrder),
        render: (text, record) => {
          if (record.weight === 0 || record.weight === 1) {
            return <span className="today-status-badge assigned">{text}</span>;
          }
          if (green(record) === record.sales_opp_stage_id) {
            return <span className="today-status-badge active">{text}</span>;
          }
          return <span className="today-status-badge in-progress">{text}</span>;
        },
      },
      {
        title: 'Created by',
        dataIndex: 'created_by',
        key: 'created_by',
        width: '11%',
        sorter: true,
        sortOrder: getColumnSortOrder('created_by', sortBy, sortOrder),
        render: (_, record) => fullNameCheck(record.first_name, record.last_name),
      },
      {
        title: 'Message',
        dataIndex: 'message',
        key: 'message',
        width: '8%',
        align: 'center',
        render: (_, record) => (
          <div className="align-center pos-rel" style={{ maxHeight: '34px' }} onClick={e => e.stopPropagation()}>
            <MessageConfigToday
              keyVar={Math.floor(Math.random() * record.id)}
              msgObj={{ ...record, name: record.client_name, client_id: record.client_id }}
              setShowPopConfirm={openChatModal}
              initiateTemplates={openChatModal}
              setIsShown={setIsShown}
              isShown={isShown}
            />
          </div>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: '11%',
        align: 'center',
        render: (_, record) => {
          const now = moment(new Date());
          if (!record.complete_date) {
            if (moment(now).isAfter(moment(record.close_date, 'YYYYMMDD'))) {
              return <span className="today-status-badge overdue">OVERDUE</span>;
            }
          }
          return null;
        },
      },
    ],
    [salesData?.salesStages, isShown, sortBy, sortOrder],
  );

  return (
    <StandardLayoutV2
      title={salesName}
      breadcrumb={[{ path: '/', title: 'Today' }, { title: salesName }]}
      classProps="flex-col-left"
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent', paddingTop: '0px' }}
    >
      <div style={{ display: 'flex', gap: 15, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        {(auth.role === 'ADMIN' || auth.role === 'MANAGER') && (
          <Select
            style={{ width: 200 }}
            value={selectedAssociateId}
            onChange={value => {
              setSelectedAssociateId(value || null);
              setPage(1);
            }}
            placeholder="All Associates"
            allowClear
            showSearch
            optionFilterProp="label"
            options={(associates || []).map(a => ({
              label: `${a.first_name || ''} ${a.last_name || ''}`.trim(),
              value: a.id,
            }))}
          />
        )}
        <Select
          style={{ width: 200 }}
          value={selectedOpportunityType}
          onChange={value => {
            setSelectedOpportunityType(value || null);
            setPage(1);
          }}
          placeholder="All Opportunity Types"
          allowClear
          showSearch
          optionFilterProp="label"
          options={opportunityTypes}
        />
        <RangePicker
          value={createdDateRange}
          onChange={dates => {
            setCreatedDateRange(dates);
            setPage(1);
          }}
          placeholder={['Created From', 'Created To']}
          format="MM/DD/YYYY"
        />
        <RangePicker
          value={dueDateRange}
          onChange={dates => {
            setDueDateRange(dates);
            setPage(1);
          }}
          placeholder={['Due From', 'Due To']}
          format="MM/DD/YYYY"
        />
        {hasActiveFilters && (
          <Button type="link" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </div>
      <Table
        className="today-table"
        columns={columns}
        dataSource={salesData?.data || []}
        rowKey={record => record.id}
        loading={isFetching}
        onChange={handleTableChange}
        pagination={{
          pageSize,
          current: page,
          total: salesData?.total || 0,
          showSizeChanger: { showSearch: false },
          showTotal: t => `Total ${t} items`,
        }}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No ${salesName}`} />,
        }}
      />
      <Drawer
        styles={{
          body: { padding: 0, overflow: 'hidden' },
        }}
        closable={false}
        push={false}
        destroyOnClose
        width={750}
        open={showChat}
        onClose={closeChat}
      >
        <div style={{ height: '100vh' }}>
          <ConversationWindow setDrawerToggle={closeChat} chatIconColor="#EBEBEB" type="right" />
        </div>
      </Drawer>
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AllSalesOpp);
